/**
 * lib/examRoomLayout.ts — a room seen from above.
 * ------------------------------------------------------------------
 * The seating screen draws a PLAN of the room, not a spreadsheet of desks:
 * four walls, the board on one of them, the door (or doors) where they really
 * are, and the desks laid out inside with the clearances a hall actually
 * keeps. An invigilator holding the sheet has to be able to stand at the door
 * and read the room, and "row 3, desk 2" means nothing until you know which
 * wall you are looking from.
 *
 * WHAT THE OFFICE SETS is only how many desks fit ACROSS and DEEP, plus where
 * the board and doors are. Everything else is computed here: the aisle in
 * front of the board is wider than the gap to the side walls, a door is never
 * covered by a desk, and the desks are centred in what is left.
 *
 * ROW 1 IS ALWAYS THE ROW NEAREST THE BOARD, whichever wall the board is on,
 * so a desk number means the same thing in every room. Turning the board to
 * another wall turns the grid with it rather than renumbering the desks.
 *
 * Coordinates are PERCENTAGES of the room box (0–100, x right, y down), so
 * the same layout draws on a screen of any size and into a PDF.
 */

export type Wall = 'front' | 'back' | 'left' | 'right';

export const WALLS: Wall[] = ['front', 'back', 'left', 'right'];

export const WALL_LABEL: Record<Wall, string> = {
  front: 'Top', back: 'Bottom', left: 'Left', right: 'Right',
};

export interface Door {
  wall: Wall;
  /** 0–1 along that wall, from left to right, or top to bottom. */
  at: number;
}

export interface RoomFixtures {
  /** Which wall the board is on. The school's default is the top. */
  board?: Wall;
  doors?: Door[];
  /** The invigilator's desk, which Cambridge's seating plan must show
   *  (Handbook 2025, 5.1.5). Absent: beside the board. */
  invigilator?: Door;
}

export interface Box { x: number; y: number; w: number; h: number }

export interface DeskBox extends Box {
  row: number;
  col: number;
}

export interface RoomLayout {
  desks: DeskBox[];
  board: Box;
  doors: (Box & { wall: Wall; swing: 'in-left' | 'in-right' })[];
  /** The invigilator's desk. */
  invigilator: Box & { wall: Wall };
  /** Where a "front of room" caption belongs, given the board's wall. */
  boardWall: Wall;
}

/* The clearances, as fractions of the room. A hall keeps more space in front
   of the board than against the side walls, and a door needs its swing. */
const EDGE = 6;          // wall to desk
const BOARD_GAP = 16;    // board wall to the first row: the invigilator's aisle
const DOOR_LENGTH = 14;  // how much of a wall a door takes
const BOARD_LENGTH = 46; // how much of its wall the board takes
const THICK = 3;         // drawn thickness of board and door

export const DEFAULT_FIXTURES: Required<Omit<RoomFixtures, 'invigilator'>> = {
  board: 'front',
  doors: [{ wall: 'left', at: 0.75 }],
};

const INVIG_LENGTH = 12;
const INVIG_THICK = 4.5;

/** Beside the board, in the aisle in front of it: where an invigilator
 *  usually sits, facing the candidates. */
export function defaultInvigilator(board: Wall): Door {
  return { wall: board, at: 0.9 };
}

/** The invigilator's desk on its wall. On the board's wall it stands in the
 *  wide aisle in front of the board; on any other wall, against the wall. */
function invigilatorBox(spot: Door, boardWall: Wall): Box & { wall: Wall } {
  const at = Math.min(1, Math.max(0, spot.at));
  const along = 2 + at * (100 - INVIG_LENGTH - 4);
  const off = spot.wall === boardWall ? THICK + 3 : 1;
  switch (spot.wall) {
    case 'front': return { x: along, y: off, w: INVIG_LENGTH, h: INVIG_THICK, wall: 'front' };
    case 'back': return { x: along, y: 100 - off - INVIG_THICK, w: INVIG_LENGTH, h: INVIG_THICK, wall: 'back' };
    case 'left': return { x: off, y: along, w: INVIG_THICK, h: INVIG_LENGTH, wall: 'left' };
    default: return { x: 100 - off - INVIG_THICK, y: along, w: INVIG_THICK, h: INVIG_LENGTH, wall: 'right' };
  }
}

/** A door's box on its wall, in room percentages. */
function doorBox(door: Door): Box & { wall: Wall; swing: 'in-left' | 'in-right' } {
  const at = Math.min(1, Math.max(0, door.at));
  const along = at * (100 - DOOR_LENGTH);
  const swing = at > 0.5 ? 'in-left' : 'in-right';
  switch (door.wall) {
    case 'front': return { x: along, y: 0, w: DOOR_LENGTH, h: THICK, wall: 'front', swing };
    case 'back': return { x: along, y: 100 - THICK, w: DOOR_LENGTH, h: THICK, wall: 'back', swing };
    case 'left': return { x: 0, y: along, w: THICK, h: DOOR_LENGTH, wall: 'left', swing };
    default: return { x: 100 - THICK, y: along, w: THICK, h: DOOR_LENGTH, wall: 'right', swing };
  }
}

function boardBox(wall: Wall): Box {
  const along = (100 - BOARD_LENGTH) / 2;
  switch (wall) {
    case 'front': return { x: along, y: 1, w: BOARD_LENGTH, h: THICK };
    case 'back': return { x: along, y: 100 - THICK - 1, w: BOARD_LENGTH, h: THICK };
    case 'left': return { x: 1, y: along, w: THICK, h: BOARD_LENGTH };
    default: return { x: 100 - THICK - 1, y: along, w: THICK, h: BOARD_LENGTH };
  }
}

/**
 * Where every desk sits.
 *
 * `cols` is across the room as the reader looks at it when the board is at
 * the top, and `rows` is the depth. With the board on a side wall the grid
 * turns with it: the rows still run away from the board, so the drawing
 * changes but the desk numbering does not.
 */
export function roomLayout(cols: number, rows: number, fixtures: RoomFixtures = {}): RoomLayout {
  const boardWall = fixtures.board ?? DEFAULT_FIXTURES.board;
  const nCols = Math.max(1, Math.floor(cols) || 1);
  const nRows = Math.max(1, Math.floor(rows) || 1);
  // In board-space: "across" runs along the board, "deep" runs away from it.
  const acrossSpan = 100 - EDGE * 2;
  const deepSpan = 100 - BOARD_GAP - EDGE;
  const acrossN = nCols;
  const deepN = nRows;
  const acrossCell = acrossSpan / acrossN;
  const deepCell = deepSpan / deepN;
  const pad = Math.min(acrossCell, deepCell) * 0.14;

  const desks: DeskBox[] = [];
  for (let row = 1; row <= nRows; row += 1) {
    for (let col = 1; col <= nCols; col += 1) {
      const across = EDGE + (col - 1) * acrossCell + pad;
      const deep = BOARD_GAP + (row - 1) * deepCell + pad;
      const w = acrossCell - pad * 2;
      const h = deepCell - pad * 2;

      // Map board-space onto the picture, so row 1 hugs the board's wall.
      let box: Box;
      if (boardWall === 'front') box = { x: across, y: deep, w, h };
      else if (boardWall === 'back') box = { x: across, y: 100 - deep - h, w, h };
      else if (boardWall === 'left') box = { x: deep, y: across, w: h, h: w };
      else box = { x: 100 - deep - h, y: across, w: h, h: w };

      desks.push({ ...box, row, col });
    }
  }

  return {
    desks,
    board: boardBox(boardWall),
    doors: (fixtures.doors ?? DEFAULT_FIXTURES.doors).map(doorBox),
    invigilator: invigilatorBox(fixtures.invigilator ?? defaultInvigilator(boardWall), boardWall),
    boardWall,
  };
}

/** Does any desk sit under a door? Used to warn rather than to move one. */
export function doorBlocked(layout: RoomLayout): boolean {
  return layout.doors.some((d) => layout.desks.some((k) => (
    k.x < d.x + d.w && k.x + k.w > d.x && k.y < d.y + d.h && k.y + k.h > d.y
  )));
}

/** Where a click on the plan lands, as a wall and a position along it. */
export function wallAt(xPct: number, yPct: number): { wall: Wall; at: number } {
  const dist: [Wall, number][] = [
    ['front', yPct], ['back', 100 - yPct], ['left', xPct], ['right', 100 - xPct],
  ];
  dist.sort((a, b) => a[1] - b[1]);
  const wall = dist[0][0];
  const along = wall === 'left' || wall === 'right' ? yPct : xPct;
  return { wall, at: Math.min(1, Math.max(0, along / 100)) };
}
